var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useRef, useState } from 'react';
import { InlineFlagsSelect, Text } from '@ui';
import { translate } from '@utils/i18n';
import { visibilitySummary } from '../utils/visibility';
import { useAttachmentFieldCommit } from '../hooks/useAttachmentFieldCommit';
const VISIBILITY_NS = 'features.building_attachments.edit_modal.visibility';
const OFFICE_VALUE = 'office';
const TECHS_VALUE = 'techs';
const PORTAL_VALUE = 'portal';
/**
 * Inline-editable `Visible to` cell (E2E-28).
 *
 * Every audience the summary names gets a checkbox, including the two that cannot be changed here:
 * "Office", which is always on, and "Customer portal" when a tenant-wide setting governs it. Both
 * render disabled with a tooltip giving the reason. Showing only the editable flags made the
 * closed state read "Office · Techs · Customer portal" above a popover containing a single "Techs"
 * checkbox, which left no way to tell whether the other two were off, broken, or simply not yours
 * to change.
 *
 * Only `is_private` ("Techs") and `show_on_customer_portal` ("Customer portal") are editable
 * flags, matching `getGeneralInfo`-style single-field commits: toggling one checkbox commits only
 * that one boolean, never both at once. Like `InlineSelect`, values shown are always the last
 * server-confirmed state — a failed commit never optimistically flips, so it simply stays put.
 */
export function AttachmentVisibilityCell({ item, buildingId, showPortalOption, translateFn }) {
    const { commit } = useAttachmentFieldCommit(buildingId, item.id);
    const [isPrivate, setIsPrivate] = useState(item.isPrivate);
    const [showOnCustomerPortal, setShowOnCustomerPortal] = useState(item.showOnCustomerPortal);
    const [error, setError] = useState(null);
    // Refetch-clobber guard (InspectionStatusRenderer.tsx:52-55) — see AttachmentNameCell for why
    // this reads `isSaving` from a ref rather than depending on the state value itself.
    const isSavingRef = useRef(false);
    useEffect(() => {
        if (isSavingRef.current)
            return;
        setIsPrivate(item.isPrivate);
        setShowOnCustomerPortal(item.showOnCustomerPortal);
    }, [item.isPrivate, item.showOnCustomerPortal]);
    const options = [
        {
            value: OFFICE_VALUE,
            label: translate('office_label', { namespace: VISIBILITY_NS }),
            disabled: true,
            disabledReason: translate('office_disabled_reason', { namespace: VISIBILITY_NS })
        },
        { value: TECHS_VALUE, label: translate('techs_label', { namespace: VISIBILITY_NS }) },
        Object.assign({ value: PORTAL_VALUE, label: translate('portal_label', { namespace: VISIBILITY_NS }) }, (showPortalOption
            ? {}
            : {
                disabled: true,
                disabledReason: translate('portal_disabled_reason', { namespace: VISIBILITY_NS })
            }))
    ];
    // Office is always on, so it is always checked — it is the disabled flag, not an unset one.
    const values = [OFFICE_VALUE, ...(!isPrivate ? [TECHS_VALUE] : []), ...(showOnCustomerPortal ? [PORTAL_VALUE] : [])];
    const handleChange = (nextValues) => __awaiter(this, void 0, void 0, function* () {
        const nextIsPrivate = !nextValues.includes(TECHS_VALUE);
        const nextShowOnCustomerPortal = nextValues.includes(PORTAL_VALUE);
        // Each toggle commits its own single field — never both at once — so diff against the
        // currently-displayed state to find which one changed.
        if (nextIsPrivate !== isPrivate) {
            isSavingRef.current = true;
            setError(null);
            const result = yield commit({ isPrivate: nextIsPrivate });
            isSavingRef.current = false;
            if (result.success) {
                setIsPrivate(result.row.isPrivate);
                setError(null);
            }
            else {
                setError(result.error);
            }
            return;
        }
        if (nextShowOnCustomerPortal !== showOnCustomerPortal) {
            isSavingRef.current = true;
            setError(null);
            const result = yield commit({ showOnCustomerPortal: nextShowOnCustomerPortal });
            isSavingRef.current = false;
            if (result.success) {
                setShowOnCustomerPortal(result.row.showOnCustomerPortal);
                setError(null);
            }
            else {
                setError(result.error);
            }
        }
    });
    return (React.createElement("span", { className: "attachments-surface__visibility-cell", "data-action-cell": true },
        React.createElement(InlineFlagsSelect, { options: options, values: values, summary: visibilitySummary(isPrivate, showOnCustomerPortal, translateFn), onChange: (nextValues) => {
                void handleChange(nextValues);
            } }),
        error && (React.createElement(Text, { variant: "body", role: "alert", className: "attachments-surface__field-error" }, error))));
}
